import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MusicAPI from '../services/musicApi';
import { Icon } from '../components/Layout';
import Footer from '../components/Footer';

export default function SubscriptionTeaser() {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPreviewSongs = async () => {
      try {
        setLoading(true);
        const response = await MusicAPI.getAllSongs(1, 10);
        if (response.success) {
          const songsData = response.data?.data || response.data || [];
          setSongs(Array.isArray(songsData) ? songsData : []);
        } else {
          setError('Failed to load preview songs');
        }
      } catch (err) {
        console.error('Error fetching preview songs:', err);
        setError('Failed to load preview songs. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPreviewSongs();
  }, []);

  const handleUpgradeClick = () => {
    navigate('/premium');
  };

  if (loading) {
    return (
      <>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
          <div className="max-w-6xl mx-auto px-4 py-8">
            <div className="flex justify-center items-center min-h-[60vh]">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fuchsia-500 mx-auto"></div>
                <p className="mt-4 text-slate-400">Loading preview songs...</p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
          <div className="max-w-6xl mx-auto px-4 py-8">
            <div className="flex justify-center items-center min-h-[60vh]">
              <div className="text-center">
                <div className="text-red-500 text-4xl mb-4">⚠️</div>
                <h3 className="text-xl font-bold text-white mb-2">Unable to Load Preview</h3>
                <p className="text-slate-400 mb-4">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white px-6 py-2 rounded-lg"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <div className="max-w-6xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="text-center space-y-4 mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-fuchsia-500 to-purple-600 bg-clip-text text-transparent">
              Preview Our Music Collection
            </h1>
            <p className="text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto">
              Enjoy these sample songs! Upgrade to premium for unlimited access to our full library.
            </p>
          </div>

          {/* Songs Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {songs.slice(0, 9).map((song) => (
              <div
                key={song._id}
                className="bg-white/5 backdrop-blur-lg rounded-xl border border-white/10 p-6 hover:border-white/20 transition-all"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-fuchsia-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <Icon.Music className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-semibold truncate">{song.title}</h3>
                    <p className="text-slate-400 text-sm truncate">{song.singer}</p>
                    <p className="text-slate-500 text-xs truncate">{song.category?.name || 'Unknown'}</p>
                  </div>
                </div>
                <div className="mt-4 text-center">
                  <span className="text-xs text-slate-500">Preview only - Upgrade for full access</span>
                </div>
              </div>
            ))}
          </div>

          {/* Call to Action */}
          <div className="text-center">
            <div className="bg-gradient-to-r from-fuchsia-500 to-purple-600 rounded-2xl p-8 mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">
                Unlock Unlimited Music
              </h2>
              <p className="text-white/90 mb-6">
                Get access to thousands of songs, ad-free listening, and exclusive features with our premium plans.
              </p>
              <button
                onClick={handleUpgradeClick}
                className="bg-white text-fuchsia-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition"
              >
                Upgrade Now
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}